import { useState, useEffect } from "react";
import { getProgressReport } from "../services/constructionReportService";

interface AmountWithCurrency {
  amount: number;
  currency?: { code: string; symbol: string; name: string };
}

export interface G702Data {
  originalContractSum?: AmountWithCurrency;
  sovUpdate?: AmountWithCurrency;
  netChangeByChangeOrders?: AmountWithCurrency;
  contractSumToDate?: AmountWithCurrency;
  totalCompletedAndStored?: AmountWithCurrency;
  totalRetainage?: AmountWithCurrency;
  totalEarnedLessRetainage?: AmountWithCurrency;
  lessPreviousCertificates?: AmountWithCurrency;
  currentPaymentDue?: AmountWithCurrency;
  balanceToFinish?: AmountWithCurrency;
  completedWorkRetainage?: AmountWithCurrency;
  storedAmountRetainage?: AmountWithCurrency;
  retainageRate?: string;
}

export interface G703LineItem {
  aiaItem?: { itemCode: number; itemName: string; itemLabel: string };
  aiaItemId?: string;
  aiaItemCode?: number;
  description?: string;
  displayOrder?: number;
  sovValue?: AmountWithCurrency;
  previousBilled?: AmountWithCurrency;
  currentApplication?: AmountWithCurrency;
  currentStoredMaterial?: AmountWithCurrency;
  totalCompletedAndStored?: AmountWithCurrency;
  balanceToFinish?: AmountWithCurrency;
  retainageAmount?: AmountWithCurrency;
  percentageComplete?: number;
}

export interface DrawPrefill {
  id: string;
  drawNumber: number;
  drawDate: string;
  toDate: string;
  inspectionDate: string;
  requestedAmount?: { amount: number };
  approvedAmount?: { amount: number } | null;
  retainageThreshold?: string;
  g702: G702Data;
  g703: { lineItems: G703LineItem[]; retainageRate: string };
}

export interface InspectionPhaseItem {
  id: string;
  itemCode: string;
  itemLabel: string;
  itemWeight: number;
  cscEstimation: string;
  isFlagged: boolean;
  isIncludedFlagInReport?: boolean;
  note: Record<string, string>;
}

export interface InspectionPhase {
  phase: string;
  phaseOrder: number;
  phaseWeight: number;
  items: InspectionPhaseItem[];
  phaseCscEstimation: number;
}

export interface InspectionSnapshotLineItem {
  id: string;
  aiaItemId: string;
  aiaItem: {
    id?: string;
    itemCode: number;
    itemName: string;
    itemLabel: string;
  };
  totalAmount: { amount: number; currencyCode: string };
  sovAmount: number;
  gcBilledPct: number;
  cscEstimation: string;
  isFlagged: boolean;
  isIncludedFlagInReport?: boolean;
  note: Record<string, string>;
}

export interface LineItem {
  id: string;
  aiaItemId: string;
  aiaItem: { itemCode: number; itemName: string; itemLabel: string };
  totalAmount: { amount: number };
  storedItemAmount: { amount: number } | null;
  sovAmount: number;
  gcBilledPct: number;
  cscEstimatePct: number | string | null;
  isFlagged: boolean;
  inspectionLienItemId: string | null;
  inspectionNote: Record<string, string> | null;
}

export interface LienWaiver {
  id: string;
  companyName: string;
  signerName: string;
  totalAmount: { amount: number };
  paidAmount: { amount: number };
  remainingAmount: { amount: number };
  retainageAmount: { amount: number };
  signedAt: string;
  isFinalPayment: boolean;
}

export interface LienWaiverDraw {
  drawId: string;
  drawNumber: number;
  waivers: LienWaiver[];
}

export interface ReportDocument {
  id: string;
  isIncluded: boolean;
  type: string;
  document: { key: string; fileName: string; url: string };
  description: string | null;
  note?: string | null;
}

export interface ProgressReportPrefill {
  draw: DrawPrefill;
  project: {
    id: string;
    name: string;
    address: string;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    description: string;
    type: string;
    settings: {
      constructionReportAuthoringFee: { amount: number; currencyCode: string };
    } | null;
  };
  stakeholders: {
    lender: { id: string; companyName: string; branchName: string };
    lendingOfficer: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      companyName: string;
    } | null;
    gc: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      companyName: string;
    } | null;
    owner: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      companyName: string;
    } | null;
    inspector: null;
  };
  inspection: {
    id: string;
    inspectorName: string;
    cscEstimation: string;
    inspectionDate: string;
    inspectionNumber: number;
    note: {
      bank?: string;
      flags?: string;
      general?: string;
      narrative?: string;
      schedule?: string;
      offsite?: string;
    };
  } | null;
  inspectionSnapshot: {
    phases?: InspectionPhase[];
    overallCscEstimation?: number;
    lineItems?: InspectionSnapshotLineItem[];
  } | null;
  lineItems: LineItem[];
  lienWaiversByDraw: LienWaiverDraw[];
  documents: ReportDocument[];
  nextReportNumber: number;
  invoiceNumber: string;
}

export interface ReportDraft {
  id: string;
  reportNumber: number | null;
  reportDate: string | null;
  invoiceNumber: string | null;
  invoiceAmount: { amount: number; currencyCode: string } | null;
  invoiceDate: string | null;
  inspectorName: string | null;
  drawAssessmentText: string | null;
  drawBulletPoints: string | null;
  changeOrderNotes: string | null;
  offsiteMaterialsNotes: string | null;
  signatureDisclaimer: string | null;
  lienWaiverDescription: string | null;
  lienWaiverNote: string | null;
  lienWaiverMinAmount: { amount: number; currencyCode: string } | null;
  bankActionItems: string | null;
  scheduleNotes: string | null;
  servicesRequested: string | null;
  recommendation: string;
  recommendationText: string | null;
  attachmentsList: string | null;
  signatoryDate: string | null;
  invoicePayableTo: string | null;
  invoiceMailTo: string | null;
  deliveryCc: string | null;
  deliveryMethod: string;
  lockAfterPublish: boolean;
  status: string;
  project: ProgressReportPrefill["project"] | null;
  stakeholders: ProgressReportPrefill["stakeholders"] | null;
  draw: ProgressReportPrefill["draw"] | null;
  inspectionSnapshot: {
    phases?: InspectionPhase[];
    overallCscEstimation?: number;
    lineItems?: InspectionSnapshotLineItem[];
  } | null;
  documents: Array<{
    id: string;
    isIncluded: boolean;
    type: string;
    document: { key: string; fileName: string; url: string };
    description: string | null;
    note?: string | null;
  }>;
  lienWaiversByDraw: LienWaiverDraw[];
}

export interface ProgressReportData {
  prefill: ProgressReportPrefill | null;
  report: ReportDraft | null;
}

const useConstructionProgressReport = (projectId: string, drawId: string) => {
  const [data, setData] = useState<ProgressReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId || !drawId) return;
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setError("");
      const { data: raw, error: apiError } = (await getProgressReport(
        projectId,
        drawId,
      )) as {
        data: { data?: ProgressReportData } | null;
        error: { message?: string } | null;
      };
      if (cancelled) return;
      setLoading(false);
      if (apiError) {
        setError(apiError.message ?? "Failed to load report data.");
        return;
      }
      const payload = raw?.data;
      if (payload) setData(payload);
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [projectId, drawId]);

  return { data, loading, error };
};

export default useConstructionProgressReport;
